import { Fragment, type ReactNode } from "react";

/**
 * A tiny, dependency-free rich-text renderer for Dinnobot's replies.
 *
 * The model returns light Markdown-ish text (short paragraphs, the occasional
 * numbered step list, **bold** UI labels, and emoji nav icons). The chat bubble
 * used to print that raw with `whitespace-pre-wrap`, so `**Employees**` showed
 * its literal asterisks. This renders a safe *subset* of Markdown so answers
 * read cleanly instead:
 *   - blank-line-separated paragraphs
 *   - unordered lists (`- `, `* `, or `• `)
 *   - ordered lists (`1. `, `2)` …) — numbering is preserved
 *   - inline `**bold**`, `*italic*` / `_italic_`, and `` `code` ``
 *
 * Everything is built from React elements, so text is auto-escaped — there is no
 * `dangerouslySetInnerHTML` and no HTML-injection surface. Emoji are ordinary
 * characters and pass straight through. Unmatched markers (a stray `*`) simply
 * render as themselves rather than breaking the output.
 */

const BULLET = /^\s*[-*•]\s+(.*)$/;
const ORDERED = /^\s*(\d+)[.)]\s+(.*)$/;

/** Split one line into React nodes, honoring inline bold / italic / code. */
function renderInline(text: string, keyBase: string): ReactNode[] {
  // Capturing split keeps the delimiters as their own chunks. Bold is matched
  // before italic so `**x**` isn't mis-read as two italics.
  const parts = text.split(
    /(\*\*[^*\n]+?\*\*|`[^`\n]+?`|\*[^*\s][^*\n]*?\*|_[^_\s][^_\n]*?_)/g,
  );
  return parts
    .filter((p) => p !== "")
    .map((part, i) => {
      const key = `${keyBase}-${i}`;
      if (part.startsWith("**") && part.endsWith("**")) {
        return (
          <strong key={key} className="font-semibold text-navy">
            {part.slice(2, -2)}
          </strong>
        );
      }
      if (part.startsWith("`") && part.endsWith("`")) {
        return (
          <code
            key={key}
            className="rounded bg-slate-100 px-1 py-0.5 text-[0.85em] font-medium"
          >
            {part.slice(1, -1)}
          </code>
        );
      }
      if (
        (part.startsWith("*") && part.endsWith("*")) ||
        (part.startsWith("_") && part.endsWith("_"))
      ) {
        return <em key={key}>{part.slice(1, -1)}</em>;
      }
      return <Fragment key={key}>{part}</Fragment>;
    });
}

export function RichText({ text }: { text: string }) {
  const lines = text.replace(/\r\n/g, "\n").split("\n");
  const blocks: ReactNode[] = [];

  // Accumulators for the block currently being built.
  let para: string[] = [];
  let bullets: string[] = [];
  let ordered: string[] = [];

  const flushPara = () => {
    if (!para.length) return;
    const key = `p-${blocks.length}`;
    blocks.push(
      <p key={key} className="leading-relaxed">
        {para.map((ln, i) => (
          <Fragment key={i}>
            {i > 0 && <br />}
            {renderInline(ln, `${key}-${i}`)}
          </Fragment>
        ))}
      </p>,
    );
    para = [];
  };

  const flushBullets = () => {
    if (!bullets.length) return;
    const key = `ul-${blocks.length}`;
    blocks.push(
      <ul key={key} className="list-disc space-y-0.5 pl-5">
        {bullets.map((item, i) => (
          <li key={i}>{renderInline(item, `${key}-${i}`)}</li>
        ))}
      </ul>,
    );
    bullets = [];
  };

  const flushOrdered = () => {
    if (!ordered.length) return;
    const key = `ol-${blocks.length}`;
    blocks.push(
      <ol key={key} className="list-decimal space-y-0.5 pl-5">
        {ordered.map((item, i) => (
          <li key={i}>{renderInline(item, `${key}-${i}`)}</li>
        ))}
      </ol>,
    );
    ordered = [];
  };

  const flushAll = () => {
    flushPara();
    flushBullets();
    flushOrdered();
  };

  for (const line of lines) {
    if (line.trim() === "") {
      flushAll();
      continue;
    }
    const bullet = line.match(BULLET);
    if (bullet) {
      flushPara();
      flushOrdered();
      bullets.push(bullet[1]);
      continue;
    }
    const num = line.match(ORDERED);
    if (num) {
      flushPara();
      flushBullets();
      ordered.push(num[2]);
      continue;
    }
    // Plain prose line — a soft break within the current paragraph.
    flushBullets();
    flushOrdered();
    para.push(line);
  }
  flushAll();

  return <div className="space-y-2">{blocks}</div>;
}
